<template>
    <div
        ref="searchWidget"
        class="search-widget fixed top-6 right-6 z-40 hidden sm:flex flex-col-reverse items-end gap-2 rounded-full text-sm sm:flex-row"
        :class="{
            'pointer-events-none': store.isDragging,
        }"
        v-bind="$attrs"
    >
        <Transition name="fade">
            <div
                v-show="searchOpen"
                class="mobile-width flex gap-2 duration-150"
            >
                <div
                    class="bg-white/70 backdrop-blur-sm shadow rounded-lg hover:bg-elevated hover:text-primary flex size-10 shrink-0 cursor-pointer items-center justify-center p-1.5 hover:bg-gray-100"
                    @click="
                        () => {
                            searchOpen = false
                            Search.clear()
                        }
                    "
                >
                    <span class="esri-icon esri-icon-close text-lg!" />
                </div>
                <div
                    class="search-panel bg-white/70 backdrop-blur-sm shadow relative top-0 right-0 z-10 flex h-10 w-xs rounded-lg duration-150"
                >
                    <div class="flex w-full items-stretch">
                        <Suspense>
                            <component
                                :is="searchComponents[searchType]"
                                :ref="
                                    (el: any) =>
                                        (searchComponentRefs[searchType] = el)
                                "
                                :active="searchOpen"
                                :result-window-class="RESULT_WINDOW_CLASS"
                            />
                        </Suspense>
                    </div>
                </div>
            </div>
        </Transition>
        <!-- <div
            class="group hover:bg-elevated hover:text-primary dark:hover:text-default border-default bg-default z-20 flex size-11 cursor-pointer items-center justify-center rounded-full border shadow-lg"
            title="Vyhledávání"
            @click="handleSearchBtnClick"
        >
            <span
                class="esri-icon esri-icon-search group-hover:font-semibold!"
            />
        </div> -->
        <button
            class="cursor-pointer bg-white/70 backdrop-blur-sm shadow rounded-lg p-2 flex justify-center items-center hover:bg-gray-100 size-10"
            title="Vyhledávání"
            @click="handleSearchBtnClick"
        >
            <span class="esri-icon esri-icon-search text-lg!" />
        </button>
    </div>
    <Teleport to="body">
        <div
            class="search-widget-result-window mobile-width bg-white/70 backdrop-blur-sm fixed top-32 rounded-2xl border border-gray-100/70 shadow-lg sm:top-20 sm:right-4 sm:w-96 md:right-6 z-20"
            :class="{
                'invisible opacity-0 duration-150': !Search.state.hasResults,
                'duration-300': Search.state.hasResults,
                'pointer-events-none':
                    store.isDragging || !Search.state.hasResults,
            }"
        >
            <div
                class="space-between flex items-center border-b border-gray-200/50 px-4 py-3"
            >
                <h3 class="text-sm font-semibold text-gray-800">
                    Výsledky hledání
                </h3>
                <button
                    ref="button"
                    type="button"
                    class="close-btn relative! top-auto! right-auto! ml-auto"
                    @click="Search.clear"
                >
                    <span class="esri-icon esri-icon-close" />
                </button>
            </div>
            <div
                class="bg-transparent px-4 py-2"
                :class="RESULT_WINDOW_CLASS"
            />
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, defineAsyncComponent, watch } from "vue"
import { useStore } from "../../store"
import { useSearchLogic } from "./SearchLogic"
import type { SearchConfig, SearchTypes } from "../../types/search"

const OPTIONS: SearchConfig = {
    types: ["address", "parcels"],
    symbols: {
        marker: {
            color: [200, 0, 0, 0.3],
            size: 12,
            outline: {
                color: [200, 0, 0, 1],
                width: 2,
            },
        },
        line: {
            color: [200, 0, 0, 0.8],
            width: 2.5,
        },
        fill: {
            color: [200, 0, 0, 0.3],
            outline: {
                color: [200, 0, 0, 1],
                width: 2,
            },
        },
    },
}

const store = useStore()
const Search = useSearchLogic()

const RESULT_WINDOW_CLASS = "search-widget-result-window-content"

const emit = defineEmits(["mesh-activated-from-search"])

watch(
    () => store.viewRightReady,
    newVal => {
        if (newVal) {
            Search.init()
        }
    },
)

const searchComponents: Record<string, unknown> = {}
const searchComponentRefs: Record<string, unknown> = {}
for (let i = 0; i < OPTIONS.types.length; i++) {
    const type = OPTIONS.types[i] as SearchTypes
    const componentName = type.charAt(0).toUpperCase() + type.slice(1)
    searchComponents[type] = defineAsyncComponent(
        () => import(`./forms/${componentName}.vue`),
    )
}

const searchOpen = ref(false)
const searchType = ref(OPTIONS.types[0] as SearchTypes)

function handleSearchBtnClick() {
    if (!store.isMeshLayerActivated) {
        store.isMeshLayerActivated = true
        emit("mesh-activated-from-search")
    }
    if (!searchOpen.value) {
        searchOpen.value = true
    } else {
        const searchFormComp = searchComponentRefs[searchType.value] as unknown
        // @ts-expect-error form components as any
        searchFormComp.formSearch()
    }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
